import {
    Activity, Award, Building2, CalendarClock, ClipboardList, Filter, Flame, Gauge,
    GitBranch, GraduationCap, Handshake, Image as ImageIcon, IndianRupee, Layers,
    LineChart, ListChecks, Mail, MapPin, Megaphone, PieChart, Radio, Siren,
    Sparkles, Target, Timer, TrendingUp, Upload, UserCheck, Users, Clock,
    BarChart3, Shuffle, HeartHandshake, Presentation, Leaf,
} from "lucide-react";

/*
 * The report index, grouped and ordered the way a CRM team actually uses it:
 * what needs doing today first, then the pipeline, the team, where leads come
 * from, ad spend, life after the sale, and admin last. Within a group the most
 * used report comes first — except Timeline, which leads the whole index
 * (asked for on 2026-10-13: it is the first thing the team opens).
 *
 * Visibility is not decided here — ReportIndex shows a report only when the
 * sidebar (hooks/use-navigation.js, which applies plan features and role
 * permissions) also offers its route. Keep the sidebar's Reports order in step
 * with this file.
 */

export const REPORT_GROUPS = [
    {
        key: "today",
        title: "Start your day",
        subtitle: "What needs attention right now — open these every morning",
        reports: [
            { route: "/reports/timeline", title: "Timeline", icon: Clock,
              description: "Every stage change — which lead, from what to what, who moved it and when" },
            { route: "/reports/action-center", title: "Action Center", icon: Siren,
              description: "Missed demos, demos at risk, urgent leads, unowned leads and follow-up backlog — worst first" },
            { route: "/reports/ai-suggestions", title: "AI Next Actions", icon: Sparkles,
              description: "The next best action on every open lead, with the reason and a suggested opener" },
            { route: "/reports/demo-desk", title: "Demo Desk", icon: CalendarClock,
              description: "Demos likely to fall through, with a ready WhatsApp message to confirm or rescue each" },
            { route: "/reports/engagement", title: "Engagement", icon: Activity,
              description: "Hot, warm and cold leads by recent activity — find leads going cold before they are lost" },
            { route: "/reports/lead-welcome", title: "Lead Welcome & Engagement", icon: HeartHandshake,
              description: "New leads from welcome email and WhatsApp to first call — who engaged, confirmed, and still needs a call" },
            { route: "/reports/demo-report", title: "Demo Report", icon: Presentation,
              description: "Demo reminders, who confirmed or moved their demo, who turned up, and the feedback after" },
            { route: "/reports/nurture", title: "Nurture", icon: Leaf,
              description: "Real leads parked for later — revisits due, who is warming up, and what the nurture messages achieved" },
        ],
    },
    {
        key: "pipeline",
        title: "Pipeline & forecast",
        subtitle: "Where the deals are, what they are worth and what will close",
        reports: [
            { route: "/reports/pipeline-activity", title: "Pipeline Activity", icon: Gauge,
              description: "Proposal and negotiation deals with value, age and follow-up — stale deals highlighted" },
            { route: "/reports/sales-forecast", title: "Sales Forecast", icon: TrendingUp,
              description: "Weighted revenue forecast per deal, with worst / likely / best case and monthly commits" },
            { route: "/reports/lead-stages", title: "Lead Stages", icon: ListChecks,
              description: "Every lead by stage — working, stalled, won, lost — split by Meta, website and manual" },
            { route: "/reports/conversion", title: "Conversion", icon: Target,
              description: "How long each lead has sat in its current stage and how often it has moved" },
            { route: "/reports/lead-potential", title: "Lead Potential", icon: Flame,
              description: "Leads split into Hot, Medium and Cold, and leads nobody has rated yet" },
        ],
    },
    {
        key: "team",
        title: "Team & activity",
        subtitle: "How each rep is doing and what the team actually did",
        reports: [
            { route: "/reports/team-performance", title: "Team Performance", icon: Users,
              description: "Activities, demos, wins, revenue and monthly targets — every rep side by side" },
            { route: "/reports/coaching", title: "Coaching", icon: GraduationCap,
              description: "What winning reps do differently, with coaching notes for each rep" },
            { route: "/reports/lead-assignment", title: "Lead Assignment", icon: Shuffle,
              description: "How fast new leads get an owner, how evenly they are shared, and what was left waiting" },
            { route: "/reports/email-analytics", title: "Email Analytics", icon: Mail,
              description: "Emails sent and received, and whether they were opened, clicked, replied or bounced" },
        ],
    },
    {
        key: "sources",
        title: "Lead sources",
        subtitle: "Where leads come from and which sources bring good ones",
        reports: [
            { route: "/reports/meta-performance", title: "Meta Performance", icon: Award,
              description: "Per Meta lead form: share of quality, disqualified and unanswered leads, with reasons" },
            { route: "/reports/lead-types", title: "Lead Source", icon: LineChart,
              description: "Leads by lead source, with each lead's follow-up status (overdue, today, none)" },
            { route: "/reports/lead-sources", title: "Lead Meta Form", icon: PieChart,
              description: "New leads by the Meta lead form they came through, with city, stage and owner" },
            { route: "/reports/channel-partners", title: "Partner Sourcing", icon: Handshake,
              description: "Which channel partners send leads, what those leads became, and who has gone quiet" },
        ],
    },
    {
        key: "ads",
        title: "Meta ads",
        subtitle: "What ad spend is buying — from today's spend to closed revenue",
        reports: [
            { route: "/reports/ad-performance", title: "Ad Performance", icon: Megaphone,
              description: "Spend by campaign, ad set and ad — CPL, CAC and ROAS against real CRM outcomes, with verdicts" },
            { route: "/reports/ad-dashboard", title: "Live Ad Spend", icon: Radio,
              description: "Today's spend, leads and CPL per running campaign, against your target and budget" },
            { route: "/reports/ad-roi", title: "Marketing ROI", icon: IndianRupee,
              description: "Meta spend beside closed revenue per product, with surplus, CAC and ROAS" },
            { route: "/reports/ad-lead-quality", title: "Lead Quality by Ad", icon: Award,
              description: "Which ads bring leads worth calling — demo rate, disqualified rate, time to first contact" },
            { route: "/reports/ad-funnel", title: "Funnel by Ad", icon: Filter,
              description: "Lead → contacted → demo → won per ad, with the drop-off at each step" },
            { route: "/reports/ad-attribution", title: "Lead Attribution", icon: GitBranch,
              description: "Each lead traced to the campaign, ad set and ad that produced it" },
            { route: "/reports/ad-creative", title: "Ad Creative", icon: ImageIcon,
              description: "Which creative wins — thumbnails, Meta rankings, video hold and thumb-stop rate" },
            { route: "/reports/ad-pacing", title: "Spend Pacing", icon: Gauge,
              description: "Daily spend against leads, flagging days where cost per lead jumped or brought nothing" },
            { route: "/reports/ad-audience", title: "Ad Audience", icon: Users,
              description: "Age, gender, placement and device — spend and cost per lead in each segment" },
            { route: "/reports/ad-geography", title: "Ad Geography", icon: MapPin,
              description: "Spend and cost per lead by region" },
            { route: "/reports/ad-rep-effectiveness", title: "Rep × Ad", icon: UserCheck,
              description: "Close rate by rep and by ad — where one rep converts what another cannot" },
            { route: "/reports/ad-time-lag", title: "Time to Revenue", icon: Timer,
              description: "Days from lead to demo to signature per ad" },
            { route: "/reports/ad-conversions", title: "Conversions to Meta", icon: Upload,
              description: "CRM outcomes waiting to be sent back to Meta so it optimises toward buyers" },
        ],
    },
    {
        key: "customers",
        title: "After the sale",
        subtitle: "Onboarded clients, implementation and renewals",
        reports: [
            { route: "/onboarded/reports/implementation", title: "Implementation Status", icon: ListChecks,
              description: "Progress per client and every milestone that is running late" },
            { route: "/onboarded/reports/amc-tracker", title: "AMC Tracker", icon: CalendarClock,
              description: "AMCs overdue or expiring soon, and the value at risk" },
            { route: "/onboarded/reports/revenue", title: "Onboarded Revenue", icon: IndianRupee,
              description: "Contract and AMC value of onboarded clients, and how many AMCs are paid" },
            { route: "/onboarded/reports/client-list", title: "Onboarded Clients", icon: Building2,
              description: "Every onboarded client with value, contact and location — and where details are missing" },
        ],
    },
    {
        key: "admin",
        title: "Admin",
        subtitle: "System records",
        reports: [
            { route: "/reports/audit-log", title: "Audit Log", icon: ClipboardList,
              description: "Who created, changed, deleted or logged in — and when" },
        ],
    },
];

/** Shown above the groups: the whole sales picture on one page. */
export const FEATURED_REPORT = {
    route: "/reports/all", title: "All-in-One Report", icon: Layers,
    description: "Every sales report on one page — one set of filters, one Excel file. Best for weekly and monthly reviews.",
};

/* Engage (hospital) reports, same idea. */
export const ENGAGE_REPORT_GROUPS = [
    {
        key: "today",
        title: "Start your day",
        subtitle: "Patients and follow-ups that need attention now",
        reports: [
            { route: "/engage/reports/timeline", title: "Timeline", icon: Clock, color: "from-slate-500 to-gray-600",
              description: "Recent calls, notes and messages on enquiries" },
            { route: "/engage/reports/engagement", title: "Engagement", icon: Activity, color: "from-purple-500 to-pink-600",
              description: "Hot / warm / cold enquiries and the ones needing a call" },
            { route: "/engage/reports/followup-completion", title: "Follow-up Completion", icon: ListChecks, color: "from-emerald-500 to-teal-600",
              description: "Completed vs overdue follow-ups, per staff member" },
            { route: "/engage/reports/no-show-rate", title: "No-Show Rate", icon: Clock, color: "from-red-500 to-orange-600",
              description: "Booked appointments vs patients who actually came" },
        ],
    },
    {
        key: "funnel",
        title: "Enquiries & conversion",
        subtitle: "How enquiries turn into patients, and where they drop",
        reports: [
            { route: "/engage/reports/enquiry-funnel", title: "Enquiry Funnel", icon: PieChart, color: "from-indigo-500 to-purple-600",
              description: "Stage counts and conversion % by source" },
            { route: "/engage/reports/conversion", title: "Conversion Analytics", icon: Gauge, color: "from-blue-500 to-indigo-600",
              description: "The step where enquiries are lost" },
        ],
    },
    {
        key: "sources",
        title: "Sources & ROI",
        subtitle: "Which channels bring patients, and what they cost",
        reports: [
            { route: "/engage/reports/source-roi", title: "Source ROI", icon: IndianRupee, color: "from-amber-500 to-orange-600",
              description: "Spend vs converted patients per channel" },
            { route: "/engage/reports/referrer-leaderboard", title: "Referrer Leaderboard", icon: TrendingUp, color: "from-yellow-500 to-orange-600",
              description: "Top referring doctors and incentives owed" },
            { route: "/engage/reports/meta-performance", title: "Meta Performance", icon: Award, color: "from-sky-500 to-blue-600",
              description: "Facebook / Instagram campaigns: enquiries, conversions, cost" },
            { route: "/engage/reports/marketing-roi", title: "Digital Marketing ROI", icon: LineChart, color: "from-cyan-500 to-blue-600",
              description: "Per-campaign CPL, CPA and conversion" },
            { route: "/engage/reports/camp-roi", title: "Health Camp ROI", icon: Target, color: "from-pink-500 to-rose-600",
              description: "Attendance, conversion and cost per patient per camp" },
        ],
    },
    {
        key: "accounts",
        title: "Corporate accounts",
        subtitle: "Contracts and renewals",
        reports: [
            { route: "/engage/reports/corporate-renewals", title: "Corporate Renewals", icon: CalendarClock, color: "from-teal-500 to-emerald-600",
              description: "Contracts expired or due for renewal" },
        ],
    },
];

export const ENGAGE_FEATURED_REPORT = {
    route: "/engage/reports", title: "Enquiry Reports Dashboard", icon: BarChart3,
    description: "Enquiries, conversions and revenue on one page — filter and group any way you need.",
};
